import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { BarChart as TechnicalIcon } from '@mui/icons-material';
import { DETAIL_KEYS, isGoodValue, formatDetailValue, cellDense } from './utils';

const TechnicalMetricsTable = ({ results }) => {
  const { t } = useTranslation();

  return (
    <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto', overflowY: 'visible' }}>
      <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <TechnicalIcon sx={{ fontSize: '1.1rem', color: '#64748b' }} />
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em', fontWeight: 600 }}>
            {t('earlyPivot.tableMetrics.title')}
          </Typography>
        </Box>
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ display: 'block', lineHeight: 1.6, maxWidth: 820, mt: 0.5 }}
          dangerouslySetInnerHTML={{ __html: t('earlyPivot.tableMetrics.subtitle') }}
        />
      </Box>
      <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ py: 0.75, fontWeight: 600, fontSize: '0.7rem', letterSpacing: '0.02em', color: 'text.secondary' }}>
              #
            </TableCell>
            <TableCell sx={{ py: 0.75, fontWeight: 600, fontSize: '0.7rem', letterSpacing: '0.02em', color: 'text.secondary' }}>
              {t('earlyPivot.tableMain.colTicker')}
            </TableCell>
            {DETAIL_KEYS.map((key) => {
              return (
                <Tooltip key={key} title={t(`earlyPivot.metrics.${key}.tooltip`)} arrow placement="top">
                  <TableCell 
                    align="right" 
                    sx={{ 
                      py: 0.75, 
                      fontWeight: 600, 
                      fontSize: '0.7rem', 
                      letterSpacing: '0.02em', 
                      color: 'text.secondary',
                      cursor: 'help',
                      '&:hover': { color: 'primary.main' }
                    }}
                  >
                    {t(`earlyPivot.metrics.${key}.name`)}
                  </TableCell>
                </Tooltip>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {results.results.map((row, i) => (
            <TableRow key={row.ticker} hover>
              <TableCell sx={{ ...cellDense, fontVariantNumeric: 'tabular-nums', color: 'text.secondary', fontSize: '0.7rem' }}>
                {i + 1}
              </TableCell>
              <TableCell sx={cellDense}>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                  {row.ticker}
                </Typography>
              </TableCell>
              {DETAIL_KEYS.map((key) => {
                const value = row.details?.[key];
                const isGood = isGoodValue(key, value);
                let color = '#64748b'; // default gray
                let fontWeight = 400;
                
                if (isGood === true) {
                  color = '#16a34a'; // green
                  fontWeight = 600;
                } else if (isGood === false) {
                  color = '#dc2626'; // red
                }
                
                return (
                  <TableCell 
                    key={key} 
                    align="right" 
                    sx={{ 
                      ...cellDense, 
                      fontVariantNumeric: 'tabular-nums',
                      color: color,
                      fontWeight: fontWeight,
                      fontSize: '0.75rem',
                    }}
                  >
                    {value != null ? formatDetailValue(key, value) : '—'}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TechnicalMetricsTable;
